import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Color, fill, HR_CERTIFICATIONS_PATH, HR_CERTIFICATIONS_ROUTE, HR_REQUIRED_TRAINING_PATH, LIST_PAGES, Size, pageKindLineText, requiredTrainingDeactivatePath,
  requiredTrainingProblem, t, type CertificationRegisterAnswer, type ColumnDef, type PageSize, type RequiredTrainingRow, type SortState, viewerScopeKey,
} from '@bms2/shared';
import { Button, Select } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { useNotice } from '../../components/controls/useNotice';
import { PageModule } from '../../shell/PageModule';
import { post, useApi } from '../records/api';
import { HrPagesNav } from './HrPagesNav';
import styles from './HrPages.module.css';

export { HR_CERTIFICATIONS_PATH, HR_CERTIFICATIONS_ROUTE, HR_REQUIRED_TRAINING_PATH };

const EXPIRING_COLUMNS: readonly ColumnDef[] = [
  { key: 'personName', label: t('hr.cert.col.person'), role: 'primary', type: 'text' },
  { key: 'requirementName', label: t('hr.cert.col.requirement'), role: 'secondary', type: 'text' },
  { key: 'expiresOn', label: t('hr.cert.col.expiresOn'), role: 'date', type: 'date' },
  { key: 'daysLeft', label: t('hr.cert.col.daysLeft'), role: 'value', type: 'number' },
];

const MISSING_COLUMNS: readonly ColumnDef[] = [
  { key: 'personName', label: t('hr.cert.col.person'), role: 'primary', type: 'text' },
  { key: 'requirementName', label: t('hr.cert.col.requirement'), role: 'secondary', type: 'text' },
  { key: 'requiredByText', label: t('hr.cert.col.requiredBy'), role: 'secondary', type: 'text' },
];

/** Where a required row applies, in words. */
function appliesTo(row: RequiredTrainingRow): string {
  return row.departmentId ? `${t('hr.cert.required.department')} · ${row.departmentName ?? ''}` : `${t('hr.cert.required.jobRole')} · ${row.jobRoleName ?? ''}`;
}

interface Draft {
  requirementTypeId: string;
  departmentId: string;
  jobRoleId: string;
}

const EMPTY: Draft = { requirementTypeId: '', departmentId: '', jobRoleId: '' };

/**
 * The Certification register (piece 34, Certifications): a view across every
 * employee over the existing completions, with what lapses inside the lead
 * time soonest first, what `RequiredTraining` asks for and nobody holds, and
 * the editor over those rows. 🔨 A row naming both a department and a job
 * role, or neither, is refused before it is sent, in the server's sentence.
 */
export function CertificationsPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const fetched = useApi<CertificationRegisterAnswer>(HR_CERTIFICATIONS_PATH, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [expiringSort, setExpiringSort] = useState<SortState | null>({ key: 'expiresOn', direction: 'asc' });
  const [missingSort, setMissingSort] = useState<SortState | null>({ key: 'personName', direction: 'asc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const problem = requiredTrainingProblem({ requirementTypeId: draft.requirementTypeId || null, departmentId: draft.departmentId || null, jobRoleId: draft.jobRoleId || null });

  const add = async () => {
    const sent = await post(HR_REQUIRED_TRAINING_PATH, { requirementTypeId: draft.requirementTypeId || null, departmentId: draft.departmentId || null, jobRoleId: draft.jobRoleId || null }, apiBaseUrl);
    setNotice(sent.ok ? { tone: 'good', text: t('hr.cert.required.added') } : { tone: 'error', text: sent.message });
    if (sent.ok) {
      setDraft(EMPTY);
      fetched.reload();
    }
  };
  const deactivate = async (row: RequiredTrainingRow) => {
    const sent = await post(requiredTrainingDeactivatePath(row.id), {}, apiBaseUrl);
    setNotice(sent.ok ? { tone: 'good', text: `${t('hr.cert.required.deactivated')} · ${row.requirementName}` } : { tone: 'error', text: sent.message });
    if (sent.ok) fetched.reload();
  };
  const openPerson = (personId: string) => navigate(`${LIST_PAGES.PersonRecord.route}/${personId}`);

  return (
    <PageModule title={t('hr.cert.title')} subtitle={pageKindLineText({ page: 'named', route: HR_CERTIFICATIONS_ROUTE })} icon="hr">
      <div className={styles.page} data-page="hr-certifications">
        <HrPagesNav current={HR_CERTIFICATIONS_ROUTE} />
        {noticeBanner}
        <AsyncStatePanel state={fetched.state} emptyAction={t('hr.cert.expiring.empty')} onRetry={fetched.reload}>
          {(answer) => (
            <>
              <section className={styles.section} data-hr-expiring={String(answer.expiring.length)}>
                <h2 className={styles.heading}>{t('hr.cert.expiring.title')}</h2>
                <p className={styles.meta}>{fill(t('hr.cert.expiring.hint'), { n: String(answer.leadDays) })}</p>
                <ListViewer
                  viewStore={{ scopeKey: viewerScopeKey('hr-certifications-expiring'), apiBaseUrl }}
                  recordType={t('hr.cert.expiring.title')}
                  views={[{ id: 'expiring', name: t('hr.cert.expiring.title') }]}
                  currentViewId="expiring"
                  onViewChange={() => undefined}
                  columns={EXPIRING_COLUMNS}
                  rows={answer.expiring}
                  idOf={(row) => row.completionId}
                  read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
                  sort={expiringSort}
                  onSortChange={setExpiringSort}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  emptyAction={t('hr.cert.expiring.empty')}
                  onNavigate={(row) => openPerson(row.personId)}
                />
              </section>

              <section className={styles.section} data-hr-missing={String(answer.missing.length)}>
                <h2 className={styles.heading}>{t('hr.cert.missing.title')}</h2>
                <p className={styles.meta}>{t('hr.cert.missing.hint')}</p>
                <ListViewer
                  viewStore={{ scopeKey: viewerScopeKey('hr-certifications-missing'), apiBaseUrl, param: 'missingView' }}
                  recordType={t('hr.cert.missing.title')}
                  views={[{ id: 'missing', name: t('hr.cert.missing.title') }]}
                  currentViewId="missing"
                  onViewChange={() => undefined}
                  columns={MISSING_COLUMNS}
                  rows={answer.missing.map((row) => ({ ...row, requiredByText: `${row.requiredBy === 'Department' ? t('hr.cert.required.department') : t('hr.cert.required.jobRole')} · ${row.requiredByName}` }))}
                  idOf={(row) => `${row.employmentId}:${row.requirementTypeId}`}
                  read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
                  sort={missingSort}
                  onSortChange={setMissingSort}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  emptyAction={t('hr.cert.missing.empty')}
                  onNavigate={(row) => openPerson(row.personId)}
                />
              </section>

              <section className={styles.section} data-hr-required={String(answer.required.filter((row) => row.isActive).length)}>
                <h2 className={styles.heading}>{t('hr.cert.required.title')}</h2>
                <p className={styles.meta}>{t('hr.cert.required.hint')}</p>
                <ul className={styles.list}>
                  {answer.required.filter((row) => row.isActive).length === 0 ? <li className={styles.meta}>{t('hr.cert.required.empty')}</li> : null}
                  {answer.required.filter((row) => row.isActive).map((row) => (
                    <li key={row.id} className={styles.item} data-hr-required-row={row.id}>
                      <span className={styles.itemMain}>
                        <span>{row.requirementName}</span>
                        <span className={styles.meta}>{appliesTo(row)}</span>
                      </span>
                      {/* 🔒 Deactivate and Add Requirement draw where the register says this viewer holds HR's write key (decision 3182). */}
                      {answer.editsRequired ? <Button size={Size.Small} color={Color.Inactive} text={[t('hr.cert.required.deactivate')]} actions={[() => void deactivate(row)]} /> : null}
                    </li>
                  ))}
                </ul>
                {answer.editsRequired ? (
                  <>
                    <div className={styles.controls} data-hr-required-editor="true">
                      <Select size={Size.Medium} label={t('hr.cert.required.requirement')} value={draft.requirementTypeId} onChange={(value) => setDraft({ ...draft, requirementTypeId: value })} options={answer.options.requirementTypes.map((row) => ({ value: row.id, label: row.name }))} placeholder={t('hr.cert.required.choose')} />
                      <Select size={Size.Medium} label={t('hr.cert.required.department')} value={draft.departmentId} onChange={(value) => setDraft({ ...draft, departmentId: value })} options={[{ value: '', label: t('hr.cert.required.none') }, ...answer.options.departments.map((row) => ({ value: row.id, label: row.name }))]} />
                      <Select size={Size.Medium} label={t('hr.cert.required.jobRole')} value={draft.jobRoleId} onChange={(value) => setDraft({ ...draft, jobRoleId: value })} options={[{ value: '', label: t('hr.cert.required.none') }, ...answer.options.jobRoles.map((row) => ({ value: row.id, label: row.name }))]} />
                      <Button size={Size.Medium} color={Color.Navy} text={[t('hr.cert.required.add')]} actions={[() => void add()]} disabled={problem !== null} />
                    </div>
                    {problem && (draft.requirementTypeId || draft.departmentId || draft.jobRoleId) ? <p className={styles.meta} data-hr-required-problem="true">{problem}</p> : null}
                  </>
                ) : <p className={styles.meta} data-hr-required-readonly="true">{t('hr.cert.required.readOnly')}</p>}
              </section>
            </>
          )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}
